import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import SEOHead from '@/components/base/SEOHead';
import { categories, comics } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <SEOHead
          title="分类未找到"
          description="您要查找的漫画分类不存在。"
        />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950">分类未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryComics = comics.filter((c) => c.category === slug);

  return (
    <>
      <SEOHead
        title={`${category.name} - 韩国漫画分类`}
        description={`爱漫画${category.name}分类 - ${category.description}。海量优质${category.name}韩漫免费在线阅读，下拉式流畅体验，每日更新。`}
        keywords={`${category.name},韩国漫画,${category.name}韩漫,免费韩漫,爱漫画`}
        canonicalUrl={`https://aimanga.com/category/${slug}`}
      />
      <Navbar />
      <main>
        <section className="relative w-full pt-20 pb-12 md:pb-16 bg-background-100">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: category.name },
            ]}
          />
          <div className="px-4 md:px-6 max-w-7xl mx-auto mt-6">
            <div className="flex flex-col lg:flex-row items-start gap-8">
              <div className="flex-1">
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-foreground-950 leading-tight">
                  {category.name}
                </h1>
                <p className="mt-4 text-lg text-foreground-600 max-w-xl leading-relaxed">
                  {category.description} —— 爱漫画为您精选优质{category.name}韩漫作品，持续更新，免费阅读。
                </p>
              </div>
              <div className="hidden lg:block w-80 aspect-[16/9] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20illustration%20with%20warm%20atmospheric%20lighting%2C%20artistic%20composition%20with%20clean%20lines%2C%20dramatic%20but%20simple%20background%2C%20digital%20art%20masterpiece%2C%20high%20quality%2C%20variety%20of%20genres%20represented&width=640&height=360&seq=category-banner-general&orientation=landscape"
                  alt="韩漫分类浏览"
                  className="w-full h-full object-cover rounded-lg"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-50">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            {categoryComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5" data-product-shop="">
                {categoryComics.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    slug={comic.slug}
                    title={comic.title}
                    cover={comic.cover}
                    status={comic.status}
                    rating={comic.rating}
                    chapters={comic.chapters}
                    tags={comic.tags}
                    isNew={comic.isNew}
                    isHot={comic.isHot}
                    updatedAt={comic.updatedAt}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500 text-lg">该分类暂无漫画，敬请期待新作上架！</p>
                <Link to="/" className="mt-4 inline-block text-primary-600 hover:text-primary-700 cursor-pointer">
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </section>

        <section className="w-full py-12 md:py-16 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              更多分类探索
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {categories.filter((c) => c.slug !== slug).map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="whitespace-nowrap px-5 py-2.5 rounded-full bg-background-50 border border-background-200 text-foreground-700 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-all cursor-pointer"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}